import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Grid,
} from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import React from 'react'
import { Event } from '../../../../../redux/event/manageEvents/types'

interface EventScheduleProps {
  handleChange: (
    eventOrValue:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
      | Date
      | null,
    fieldName?: string,
  ) => void
  formData: Partial<Event>
  error: {
    startDate: string
    endDate: string
    venue: string
    importance: string
    googleMeetLink: string
    postSurveyURL: string
  }
}

const EventSchedule: React.FC<EventScheduleProps> = ({ handleChange, formData, error }) => {
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    handleChange(event)
  }

  const getAdjustedDateTime = (selectedDate: Date | null, referenceDate: Date) => {
    if (!selectedDate) return null

    const selected = new Date(selectedDate)
    const reference = new Date(referenceDate)

    // If the selected time is before or equal to the reference time, add 5 minutes to the reference time
    if (selected <= reference) {
      const adjusted = new Date(reference.getTime() + 5 * 60 * 1000) // Add 5 minutes
      return adjusted
    }

    return selected
  }

  return (
    <Grid container spacing={2} direction='column'>
      <Grid>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label='Event Start Date & Time'
            slotProps={{
              textField: {
                required: true,
                fullWidth: true,
                error: !!error.startDate,
                helperText: error.startDate || '',
                id: 'start-date',
              },
              field: { clearable: true },
              actionBar: {
                actions: ['clear', 'cancel', 'accept'],
              },
            }}
            ampm
            ampmInClock
            closeOnSelect={false}
            timeSteps={{ hours: 1, minutes: 1 }}
            disablePast
            value={formData.startDate}
            maxDateTime={formData.endDate ? formData.endDate : undefined}
            onChange={(date) => {
              const adjusted = getAdjustedDateTime(date, new Date())
              handleChange(adjusted, 'startDate')
            }}
            minDateTime={new Date()}
          />
        </LocalizationProvider>
      </Grid>
      <Grid>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label='Event End Date & Time'
            slotProps={{
              textField: {
                required: true,
                fullWidth: true,
                error: !!error.endDate,
                helperText: error.endDate || '',
                id: 'end-date',
                disabled: !formData.startDate,
              },
              field: { clearable: true },
              actionBar: {
                actions: ['clear', 'cancel', 'accept'],
              },
            }}
            ampm
            ampmInClock
            closeOnSelect={false}
            disablePast
            timeSteps={{ hours: 1, minutes: 1 }}
            value={formData.endDate}
            minDateTime={formData.startDate ? formData.startDate : undefined}
            onChange={(date) => {
              const adjusted = getAdjustedDateTime(date, formData.startDate || new Date())
              handleChange(adjusted, 'endDate')
            }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid container spacing={2} direction='row'>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            id='venue'
            name='venue'
            label='Event Venue'
            required
            value={formData.venue}
            onChange={handleChange}
            placeholder='Enter event venue'
            helperText={error.venue}
            error={!!error.venue}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth required error={!!error.importance}>
            <InputLabel id='importance' error={!!error.importance}>
              Importance
            </InputLabel>
            <Select
              labelId='importance'
              value={formData.importance}
              label='Importance'
              onChange={handleSelectChange}
              // onBlur={handleBlur}
              name='importance'
              error={!!error.importance}
              inputProps={{
                id: 'importance',
              }}
            >
              <MenuItem value='optional' id='optional'>
                Optional
              </MenuItem>
              <MenuItem value='required' id='required'>
                Required
              </MenuItem>
            </Select>
            <FormHelperText>{error.importance}</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2} direction='row'>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            id='googleMeetLink'
            name='googleMeetLink'
            label='Google Meet Link'
            value={formData.googleMeetLink}
            onChange={handleChange}
            placeholder='Enter google meet link'
            helperText={error.googleMeetLink}
            error={!!error.googleMeetLink}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            id='postSurveyURL'
            name='postSurveyURL'
            label='Post Survey Link'
            value={formData.postSurveyURL}
            onChange={handleChange}
            placeholder='Enter post survey link'
            helperText={error.postSurveyURL}
            error={!!error.postSurveyURL}
            fullWidth
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

export default EventSchedule
